/*
 * /multi-track — browser multitrack recorder.
 *
 * ── What was measured before this rewrite (RU shard, 30 days) ────────────
 *   955 sessions opened the page
 *    49 (5.1 %) got as far as the microphone prompt
 *    26 (2.7 %) pressed record
 *     4 (0.4 %) downloaded anything
 *   67.5 % of takes used ONE microphone — on a multitrack recorder
 *   median take: 40 s · longest in 30 days: 3 min 45 s
 *   limit_block_exposure: seen:0 on 1010 of 1013 mounts (unreachable wall)
 *
 * The first screen was a single "Scan devices" button on an empty page: it
 * demanded a microphone permission before showing anything at all, and 95 %
 * of arrivals declined to find out what was behind it. So the console is now
 * present and legible from the first paint, the one action that needs no
 * permission (tab audio) is offered next to the one that does, and the shape
 * of a lane is visible before any device is open.
 *
 * Everything else in this directory follows from two facts the old build did
 * not survive: audio does not fit in the heap (sink.js, review.js) and N
 * recorders do not start at the same instant (recorder.js, exporter.js).
 */

import { Lane, Session, COLORS, pickMime, extForMime, dbfs, RECORD_KBPS } from './recorder.js';
import { openSink, opfsAvailable, durableAvailable, configureSinkWorker, requestPersistence,
    storageHeadroom, findOrphanSessions, dropSession, sweepStale } from './sink.js';
import { listMicrophones, openMic, openDisplayAudio, displayAudioLikely, describeStream } from './sources.js';
import { ReviewDeck } from './review.js';
import { FORMATS, renderStem, renderMix, planNames, buildCueSheet, mixFits, estimateMixBytes } from './exporter.js';
import { laneMarkup, drawWave, waveColors, fmtDb, meterPos, esc } from './lanes.js';
import { openFloatingTransport, pipSupported } from './pip.js';

const CFG = window.__MT_CFG || {};
const T = CFG.t || {};
const t = (k, vars) => {
    let s = T[k] != null ? T[k] : k;
    if (vars) for (const v in vars) s = s.split(':' + v).join(vars[v]);
    return s;
};
const SLUG = 'multi-track';

const $ = (id) => document.getElementById(id);
const ae = (f, extra) => {
    try { if (window.__ae) window.__ae('feature_use', Object.assign({ slug: SLUG, f }, extra || {})); } catch (_) {}
};

/* ── state ──────────────────────────────────────────────────────────────── */
let state = 'locked';                 // locked | ready | recording | paused | review
let session = null;
let deck = null;
let candidates = [];                  // discovered sources not yet recording
let permissionAsked = false;
let monitor = null;
let floatWin = null;
let timerRAF = 0;
let waveRAF = 0;
let restoreOffer = null;
configureSinkWorker(CFG.sinkWorkerUrls || []);
let sinkLane = durableAvailable() ? 'opfs-worker' : (opfsAvailable() ? 'opfs' : 'memory');

const el = {};
[
    'mtRoot', 'mtSrcLocked', 'mtSrcList', 'mtSrcNote', 'btnAllow', 'btnRescan', 'btnTab',
    'selRate', 'selKbps', 'chkCountdown', 'mtMonSlot', 'mtSettings',
    'mtExport', 'selFmt', 'selKbps', 'mtKbpsField', 'btnSave', 'mtExpProg', 'mtExpBar', 'mtExpText', 'mtWhat',
    'btnRec', 'btnAgain', 'btnPause', 'btnStop', 'mtTimer', 'mtState', 'mtMeta', 'btnFloat', 'btnMark',
    'mtLanes', 'mtGhost', 'mtRestore', 'mtRestoreText', 'btnRestore', 'btnDropRestore',
    'mtScrub', 'btnPlay', 'rngSeek', 'mtTime', 'mtCount', 'mtMarkList', 'mtNotice',
].forEach((id) => { el[id] = $(id); });

/* ── boot ───────────────────────────────────────────────────────────────── */
init();

function init() {
    if (!supported()) return;

    setState('locked');
    renderCandidates();

    el.btnAllow.addEventListener('click', onAllow);
    el.btnRescan.addEventListener('click', () => rescan(true));
    if (el.btnTab) {
        el.btnTab.hidden = !displayAudioLikely();
        el.btnTab.addEventListener('click', onAddTabAudio);
    }
    el.btnRec.addEventListener('click', onRecord);
    el.btnAgain.addEventListener('click', onRecord);
    el.btnPause.addEventListener('click', onPauseToggle);
    el.btnStop.addEventListener('click', () => stopRecording());
    el.btnMark.addEventListener('click', () => addMarker());
    el.btnSave.addEventListener('click', onSave);
    el.btnPlay.addEventListener('click', onPlayToggle);
    el.rngSeek.addEventListener('input', onSeek);

    el.btnFloat.hidden = !pipSupported();
    el.btnFloat.addEventListener('click', onFloat);

    el.mtWhat.addEventListener('click', onWhatClick);
    el.selFmt.addEventListener('change', onFormatChange);
    el.selKbps.addEventListener('change', () => {
        if (session) session.kbps = parseInt(el.selKbps.value, 10) || 192;
        updateExportPanel();
    });

    /* ONE delegated set of listeners on the lane container, bound once at
       boot. The old build re-bound them per recording, which double-fired
       every control from the second take onward. */
    el.mtLanes.addEventListener('click', onLaneClick);
    el.mtLanes.addEventListener('input', onLaneInput);
    el.mtSrcList.addEventListener('click', onSourceClick);
    el.mtSrcList.addEventListener('change', onSourceChange);

    document.addEventListener('keydown', onKey);
    window.addEventListener('beforeunload', onUnload);
    document.addEventListener('visibilitychange', () => {
        if (document.visibilityState === 'visible' && state === 'recording') acquireWake();
    });

    // The monitor switch has to exist BEFORE a microphone does, or it cannot
    // be found until it is too late to want it (mic-monitor.js, step 1).
    try {
        if (window.MicMonitor) {
            window.MicMonitor.control({
                tool: 'mic-monitor', mount: el.mtMonSlot, hintMount: el.mtSettings,
                labels: { label: t('monitor'), hint: t('monitor_hint') },
            });
        }
    } catch (_) {}

    offerRecovery();
    sweepStale();
    updateCaps();
    window.addEventListener('resize', () => scheduleWaveDraw());
}

function supported() {
    const ok = typeof MediaRecorder !== 'undefined'
        && navigator.mediaDevices && navigator.mediaDevices.getUserMedia;
    if (!ok) {
        notice(t('browser_unsupported'), 'bad');
        el.btnAllow.disabled = true;
        if (el.btnTab) el.btnTab.hidden = true;
    }
    return ok;
}

/* ── capability disclosure ──────────────────────────────────────────────── */
function updateCaps() {
    /* Three distinct states, because "on disk" and "survives a crash" are NOT
       the same claim and were conflated once already:
         opfs-worker — in place and flushed; a dead tab costs the last chunk
         opfs        — off the heap, but atomic-on-close: a dead tab costs it all
         memory      — in the tab; keep takes short */
    const bits = [];
    bits.push(sinkLane === 'opfs-worker' ? t('cap_disk')
        : sinkLane === 'opfs' ? t('cap_disk_fragile') : t('cap_memory'));
    if (!displayAudioLikely()) bits.push(t('cap_no_tab'));
    el.mtSrcNote.textContent = bits.join(' · ');

    if (sinkLane !== 'memory') {
        requestPersistence();
        storageHeadroom().then((free) => {
            if (free == null) return;
            /* Four lanes at the rate we actually record. This read 128 while
               recording ran at 160, so it promised 25% more hours than fit —
               the direction that runs someone out of disk mid-take. The
               constant is imported, not retyped: that is how it drifted. */
            const hours = free / (4 * RECORD_KBPS * 1000 / 8 * 3600);
            if (hours < 1) notice(t('low_storage'), 'warn');
        });
    }
}

/* ── sources ────────────────────────────────────────────────────────────── */
async function onAllow() {
    el.btnAllow.disabled = true;
    el.btnAllow.textContent = t('scanning');
    ae('perm_request');
    try {
        // One throwaway stream: enumerateDevices() returns blank labels until
        // permission exists, so this is the only way to learn device names.
        const probe = await window.__tmbGum({ audio: true });
        noteDeviceRate(describeStream(probe).sampleRate);
        probe.getTracks().forEach((x) => x.stop());
        permissionAsked = true;
        await rescan(false);
    } catch (err) {
        el.btnAllow.disabled = false;
        el.btnAllow.textContent = t('allow_mics');
        ae('perm_denied', { name: String((err && err.name) || err).slice(0, 30) });
        // MicManager owns the whole denied/blocked/busy taxonomy and the
        // recovery modal that goes with it — this tool must not invent a
        // second, worse copy of that conversation.
        try {
            if (window.MicManager) {
                const m = new window.MicManager();
                window.__toast && window.__toast(m.getErrorMessage
                    ? m.getErrorMessage(err) : t('permission_denied'), 'error');
                if (m.showRecoveryModal && err && err.name === 'NotAllowedError') {
                    m.showRecoveryModal('denied', onAllow);
                }
                return;
            }
        } catch (_) {}
        window.__toast && window.__toast(t('permission_denied'), 'error');
    }
}

async function rescan(fromButton) {
    try {
        const mics = await listMicrophones();
        const known = new Set(candidates.map((c) => c.deviceId));
        const active = new Set((session ? session.lanes : []).map((l) => l.deviceId));
        mics.forEach((m, i) => {
            if (known.has(m.deviceId) || active.has(m.deviceId)) return;
            candidates.push({
                key: 'mic:' + m.deviceId,
                kind: 'mic',
                deviceId: m.deviceId,
                label: m.label || (t('track') + ' ' + (candidates.length + 1)),
                armed: candidates.length === 0,   // first input pre-armed: one click to record
            });
        });
        el.btnAllow.disabled = false;
        el.btnAllow.textContent = t('allow_mics');
        if (!candidates.length) {
            notice(t('no_devices'), 'warn');
        } else {
            setState('ready');
            clearNotice();
        }
        renderCandidates();
        ae('devices_found', { n: candidates.length, rescan: fromButton ? 1 : 0 });
    } catch (err) {
        window.__toast && window.__toast(t('device_error'), 'error');
    }
}

async function onAddTabAudio() {
    el.btnTab.disabled = true;
    try {
        const { stream, label } = await openDisplayAudio();
        candidates.push({
            key: 'display:' + Date.now(),
            kind: 'display',
            deviceId: '',
            label: label || t('src_tab'),
            armed: true,
            stream,
        });
        setState(state === 'locked' ? 'ready' : state);
        clearNotice();
        renderCandidates();
        ae('tab_audio_added');
        // Recording already running: adopt it as a new live lane immediately.
        if (state === 'recording' || state === 'paused') await armLateLane(candidates[candidates.length - 1]);
    } catch (err) {
        const code = (err && err.code) || 'failed';
        ae('tab_audio_failed', { code });
        if (code !== 'declined') {
            window.__toast && window.__toast(
                code === 'no_audio' ? t('tab_no_audio') : t('tab_failed'), 'warning');
        }
    } finally {
        el.btnTab.disabled = false;
    }
}

function renderCandidates() {
    const rows = candidates.map((c, i) => {
        const live = isLive(c);   // ONE definition of «busy» — see isLive()
        return '<li class="mt-src' + (live ? ' live' : '') + '">'
            + '<input type="checkbox" id="src-' + i + '" data-i="' + i + '"'
            + (c.armed ? ' checked' : '') + (live ? ' disabled' : '') + '>'
            + '<label for="src-' + i + '">'
            + '<span class="mt-src-ico" aria-hidden="true">' + (c.kind === 'display' ? '🖥️' : '🎙') + '</span>'
            + '<span class="mt-src-name">' + esc(c.label) + '</span></label>'
            + (live
                ? '<span class="mt-src-live">' + esc(t('src_recording')) + '</span>'
                : '<button type="button" class="mt-src-test" data-i="' + i + '">'
                  + esc(t('test')) + '</button>'
                  + '<span class="mt-src-vu"><span class="mt-src-vu-f" data-vu="' + i + '"></span></span>')
            + '</li>';
    }).join('');

    el.mtSrcList.innerHTML = rows;
    el.mtSrcList.hidden = !candidates.length;
    el.mtSrcLocked.hidden = candidates.length > 0;
    el.btnRescan.hidden = !permissionAsked;
    updateRecButton();
    updateGhost();
}

function armedCandidates() { return candidates.filter((c) => c.armed && !isLive(c)); }
function isLive(c) {
    if (state !== 'recording' && state !== 'paused' && state !== 'finishing') return false;
    return !!(session && session.lanes.some((l) => l.srcKey === c.key));
}

function onSourceChange(e) {
    const cb = e.target.closest('input[type=checkbox]');
    if (!cb) return;
    const c = candidates[parseInt(cb.dataset.i, 10)];
    if (c) c.armed = cb.checked;
    updateRecButton();
    updateGhost();
}

/* A source test is the cheapest way to answer "is this the right mic?" —
   the question that makes people pick one input and hope. It opens the
   device for five seconds and shows the level, then closes it again. */
async function onSourceClick(e) {
    const btn = e.target.closest('.mt-src-test');
    if (!btn) return;
    const i = parseInt(btn.dataset.i, 10);
    const c = candidates[i];
    if (!c || btn.disabled) return;
    const vu = el.mtSrcList.querySelector('[data-vu="' + i + '"]');
    const row = btn.closest('.mt-src');
    if (row) row.classList.add('testing');
    btn.disabled = true;

    let stream = null, ctx = null, raf = 0;
    try {
        stream = c.stream ? c.stream : await openMic(c.deviceId, 0);
        ctx = new AudioContext();
        const src = ctx.createMediaStreamSource(stream);
        const an = ctx.createAnalyser();
        an.fftSize = 1024;
        src.connect(an);
        const buf = new Float32Array(an.fftSize);
        const until = performance.now() + 5000;
        const step = () => {
            if (performance.now() > until) { cleanup(); return; }
            an.getFloatTimeDomainData(buf);
            let p = 0;
            for (let k = 0; k < buf.length; k++) { const a = Math.abs(buf[k]); if (a > p) p = a; }
            if (vu) vu.style.width = Math.round(meterPos(p) * 100) + '%';
            raf = requestAnimationFrame(step);
        };
        step();
    } catch (err) {
        cleanup();
        window.__toast && window.__toast(t('device_error'), 'warning');
    }

    function cleanup() {
        cancelAnimationFrame(raf);
        if (vu) vu.style.width = '0';
        if (stream && !c.stream) stream.getTracks().forEach((x) => x.stop());
        if (ctx) { try { ctx.close(); } catch (_) {} }
        if (row) row.classList.remove('testing');
        btn.disabled = false;
    }
}


/**
 * Tell the quality gate what the hardware is really capable of.
 *
 * `data-qt-source` lowers the ceiling to the source material, which is
 * exactly right here: a 48 kHz microphone cannot be made into a 96 kHz
 * recording by ticking a box, and offering the higher rate as a paid step
 * would be selling an upsample. The attribute is only ever set DOWNWARD —
 * a device that does run at 96 kHz leaves the tier ceiling in charge.
 */
function noteDeviceRate(rate) {
    if (!(rate > 0)) return;
    try {
        el.selRate.setAttribute('data-qt-source', String(rate));
        // Options above what the device can do are not a paid step, they are
        // noise — take them off the list rather than padlocking them.
        Array.prototype.forEach.call(el.selRate.options, (o) => {
            const v = parseInt(o.value, 10);
            o.hidden = v > rate;
            if (o.hidden && o.selected) el.selRate.value = String(rate);
        });
        if (!Array.prototype.some.call(el.selRate.options, (o) => !o.hidden && o.value === el.selRate.value)) {
            const best = Array.prototype.filter.call(el.selRate.options, (o) => !o.hidden)[0];
            if (best) el.selRate.value = best.value;
        }
        // Re-gate: the ceiling changed under the primitive's feet.
        if (window.__qualityTier && window.__qualityTier.gate) window.__qualityTier.gate(el.selRate, 'hz');
        ae('device_rate', { rate });
    } catch (_) {}
}

/* ── recording ──────────────────────────────────────────────────────────── */
function updateRecButton() {
    const n = armedCandidates().length;
    const canArm = state === 'ready' || state === 'locked' || state === 'review';
    el.btnRec.disabled = !canArm || n === 0;
    const again = state === 'review';
    const recLabel = again ? t('record_again') : t('start_recording');
    el.btnRec.title = recLabel;
    el.btnRec.setAttribute('aria-label', recLabel);
    const rolling = state === 'recording' || state === 'paused' || state === 'finishing';
    el.btnAgain.disabled = !n;
    el.mtCount.textContent = (rolling || again) ? ''
        : (n ? t('n_armed', { n }) : (candidates.length ? t('pick_one') : ''));
}

async function onRecord() {
    if (state === 'recording' || state === 'paused') return;

    if (state === 'review') {
        // Files stay in OPFS either way — the recovery offer can bring an
        // unsaved take back for a week — but losing the console you are
        // looking at should still be a decision, not a side effect.
        if (session && !session.saved && !window.confirm(t('discard_confirm'))) return;
        await resetSession();
    }

    const armed = armedCandidates();
    if (!armed.length) return;

    el.btnRec.disabled = true;
    if (el.chkCountdown && el.chkCountdown.checked) {
        const ok = await countdown();
        if (!ok) { el.btnRec.disabled = false; return; }
    }

    session = new Session('s' + Date.now().toString(36));
    const rate = parseInt(el.selRate.value, 10) || 48000;
    session.ensureContext(rate);
    session.rate = rate;
    // Export bitrate is read at export time now; recording uses a fixed,
    // measured-transparent value (see RECORD_KBPS in recorder.js).
    session.kbps = parseInt(el.selKbps.value, 10) || 192;

    const mime = pickMime(false);
    const ext = extForMime(mime);
    if (!mime) { notice(t('browser_unsupported'), 'bad'); el.btnRec.disabled = false; return; }

    /* The level meter runs on the audio thread, so the module has to be
       registered on THIS context before any lane can attach a node. Failure
       is not fatal — the take records without meters — but it is recorded,
       because a flat meter and a silent mic look identical. */
    const meterReady = await loadMeter(session.ctx);
    session.meterReady = meterReady;

    /* Open every device AT ONCE.
       ────────────────────────────────────────────────────────────────────
       This loop used to be sequential — `await openMic`, then `await
       openSink`, per lane — so device 2's capture began after device 1's
       microphone AND its OPFS file had been created. Measured factorially on
       two devices (sequential vs concurrent open × raw vs graph recording,
       4 reps each), the cost of that ordering is the whole defect:

           sequential open : lags 50, 50, 60, 50 ms   mean 52.5, worst 60
           concurrent open : lags  0,  0,  9,  0 ms   mean  2.3, worst  9

       Routing the recorders through the shared AudioContext instead of the
       raw streams was also measured and changes nothing (52.50 → 52.66 ms
       sequential, 2.50 → 2.34 concurrent), so it is not done: it would add a
       resampling stage for no gain.

       The `getUserMedia` calls are ISSUED in one synchronous turn — that is
       what matters, not the awaiting — and only then are sinks made and lanes
       built. A device that fails is reported by name and the rest still
       record. */
    const opens = await Promise.all(armed.map((c) => (
        c.stream ? Promise.resolve({ c, stream: c.stream })
            : openMic(c.deviceId, rate).then((stream) => ({ c, stream }),
                (err) => ({ c, err }))
    )));

    const live = opens.filter((o) => o.stream);
    opens.filter((o) => o.err).forEach((o) => {
        window.__toast && window.__toast(t('device_error_named', { name: o.c.label }), 'warning');
    });

    // Sinks are independent of each other and of capture, so they open together
    // too — and by now every microphone is already running.
    const sinks = await Promise.all(live.map((o) => openSink({
        sessionId: session.id,
        laneId: o.c.key.replace(/[^a-z0-9]+/gi, ''),
        ext,
    }).then((sink) => sink, () => null)));

    let opened = 0;
    live.forEach((o, i) => {
        const sink = sinks[i];
        if (!sink) {
            try { o.stream.getTracks().forEach((t2) => t2.stop()); } catch (_) {}
            window.__toast && window.__toast(t('device_error_named', { name: o.c.label }), 'warning');
            return;
        }
        sinkLane = sink.lane;
        const lane = new Lane({
            id: 'L' + session.lanes.length,
            kind: o.c.kind,
            label: o.c.label,
            color: COLORS[session.lanes.length % COLORS.length],
            stream: o.stream, ctx: session.ctx, sink, mime,
            settings: describeStream(o.stream),
        });
        lane.srcKey = o.c.key;
        lane.deviceId = o.c.deviceId;
        lane.onFail = onLaneFail;
        if (meterReady) lane.attachMeter(window.AudioWorkletNode);
        session.add(lane);
        opened++;
    });

    if (!opened) {
        el.btnRec.disabled = false;
        notice(t('no_inputs_opened'), 'bad');
        session = null;
        return;
    }

    buildLaneStrips();
    setState('recording');

    /* Let every lane's capture actually begin before any recorder does.
       Concurrent opening removed the systematic stagger, but a COLD first take
       could still come out ~50 ms apart while the pipelines warm at different
       rates. This waits on evidence — the meter's first window per lane — not
       on a guessed delay, and gives up after 600 ms so a lane with no meter
       cannot hold the take. */
    const settledMs = await session.whenAllFlowing(600);

    session.startWall = performance.now();
    session.lanes.forEach((l) => l.start());
    startTimer();
    scheduleWaveDraw();
    acquireWake();
    attachMonitor();
    updateCaps();
    renderCandidates();

    ae('record_start', {
        tracks: session.lanes.length,
        rate, settledMs,
        tab: session.lanes.filter((l) => l.kind === 'display').length,
        sink: sinkLane,
        meter: session.lanes[0] && session.lanes[0].meterNode ? 1 : 0,
    });
}

/** A source added while the tape is already rolling joins from now on. */
async function armLateLane(c) {
    if (!session || (state !== 'recording' && state !== 'paused')) return;
    try {
        const mime = pickMime(false);
        const stream = c.stream || await openMic(c.deviceId, session.rate);
        const sink = await openSink({
            sessionId: session.id,
            laneId: c.key.replace(/[^a-z0-9]+/gi, ''),
            ext: extForMime(mime),
        });
        const lane = new Lane({
            id: 'L' + session.lanes.length,
            kind: c.kind, label: c.label,
            color: COLORS[session.lanes.length % COLORS.length],
            stream, ctx: session.ctx, sink, mime,
            settings: describeStream(stream),
        });
        lane.srcKey = c.key;
        lane.deviceId = c.deviceId;
        lane.onFail = onLaneFail;
        if (session.meterReady) lane.attachMeter(window.AudioWorkletNode);
        session.add(lane);
        lane.start();
        buildLaneStrips();
        renderCandidates();
        ae('lane_added_live');
    } catch (_) {
        window.__toast && window.__toast(t('device_error_named', { name: c.label }), 'warning');
    }
}

function onLaneFail(lane) {
    const strip = el.mtLanes.querySelector('[data-lane="' + lane.id + '"]');
    if (strip) {
        strip.classList.add('dead');
        const note = strip.querySelector('[data-role=note]');
        if (note) {
            note.hidden = false;
            note.textContent = t('device_disconnected', { name: lane.label });
        }
    }
    ae('lane_lost', { kind: lane.kind });
    window.__toast && window.__toast(t('device_disconnected', { name: lane.label }), 'warning');
    // Every other lane keeps going. Ending the session because one USB mic
    // was nudged would throw away the rest of a take that is fine.
    if (session && session.liveLanes.length === 0) stopRecording();
}

function onPauseToggle() {
    if (!session) return;
    if (state === 'recording') {
        session.lanes.forEach((l) => l.pause());
        session.elapsedMs = performance.now() - session.startWall;
        setState('paused');
    } else if (state === 'paused') {
        session.startWall = performance.now() - session.elapsedMs;
        session.lanes.forEach((l) => l.resume());
        setState('recording');
        startTimer();
    }
}

async function stopRecording() {
    if (state !== 'recording' && state !== 'paused') return;
    const wasElapsed = state === 'paused' ? session.elapsedMs : (performance.now() - session.startWall);
    session.elapsedMs = wasElapsed;
    cancelAnimationFrame(timerRAF);
    cancelAnimationFrame(waveRAF);
    releaseWake();
    detachMonitor();
    closeFloat();

    setState('finishing');
    await Promise.all(session.lanes.map((l) => l.stop().catch(() => null)));

    const done = session.doneLanes;
    ae('record_stop', {
        tracks: session.lanes.length,
        saved: done.length,
        duration: Math.round(wasElapsed / 1000),
        bytesMB: Math.round(session.totalBytes() / 1048576),
        clipped: done.filter((l) => l.clipWindows > 0).length,
        tabSilent: done.filter((l) => l.kind === 'display' && dbfs(l.peakHold) < -45).length,
        markers: session.markers.length,
        sink: sinkLane,
    });

    if (!done.length) {
        notice(t('nothing_recorded'), 'bad');
        setState(candidates.length ? 'ready' : 'locked');
        return;
    }

    deck = new ReviewDeck(session.ctx);
    deck.onTick = onDeckTick;
    deck.onEnd = () => { el.btnPlay.textContent = t('play'); };
    await deck.load(done);
    deck.applyMix();

    setState('review');
    buildLaneStrips();
    scheduleWaveDraw();
    updateExportPanel();
    renderMarkerList();
    renderCandidates();                 // inputs are free again — drop "recording"
    onDeckTick(0, deck.duration);       // the clock reads the take, not 0:00 / 0:00

    /* A tab-audio lane that recorded near-silence.
       ─────────────────────────────────────────────────────────────────────
       Chrome's screen picker carries a SEPARATE "share tab audio" checkbox,
       and forgetting it is the number-one way this feature fails: the browser
       still hands back an audio track, so nothing errors, and the lane records
       a flat -30 dB of nothing. Measured under automation — a tab playing a
       440 Hz tone at 0.3 amplitude arrived at peak 0.042 (about 32 dB down),
       which the level chip happily calls "норма".
       The existing empty-track guard cannot catch this: there IS a track. So
       the verdict is made on the LEVEL, and it names the checkbox rather than
       saying "quiet", because that is the thing the user has to go and fix. */
    const mute = done.filter((l) => l.kind === 'display' && dbfs(l.peakHold) < -45);
    if (mute.length) notice(t('tab_silent_note'), 'warn');

    const hot = done.filter((l) => l.clipWindows > 0);
    if (hot.length) notice(t('clip_note', { names: hot.map((l) => l.label).join(', ') }), 'warn');
}


/**
 * Tear the finished session down and hand the console back in its ready
 * state. A take that WAS saved has no reason to keep occupying the user's
 * storage quota; one that was not is left on disk so the recovery offer can
 * still reach it.
 */
async function resetSession() {
    if (deck) { deck.dispose(); deck = null; }
    if (session) {
        const id = session.id, saved = session.saved;
        await session.closeContext();
        if (saved) dropSession(id);
    }
    session = null;
    el.mtLanes.innerHTML = '';
    el.mtLanes.dataset.built = '';
    el.mtMarkList.hidden = true;
    el.mtMarkList.innerHTML = '';
    el.mtTimer.textContent = '00:00:00';
    el.mtExpProg.hidden = true;
    clearNotice();
    // Sources that were locked as "recording" are selectable again.
    candidates.forEach((c) => { if (c.kind === 'display') c.armed = !!c.stream; });
    setState(candidates.length ? 'ready' : 'locked');
    renderCandidates();
    updateGhost();
    ae('session_reset');
}

/* ── timers, meters, waveforms ──────────────────────────────────────────── */
function startTimer() {
    cancelAnimationFrame(timerRAF);
    const step = () => {
        if (state !== 'recording') return;
        session.elapsedMs = performance.now() - session.startWall;
        el.mtTimer.textContent = clock(session.elapsedMs);
        timerRAF = requestAnimationFrame(step);
    };
    step();
}

function scheduleWaveDraw() {
    cancelAnimationFrame(waveRAF);
    const colors = waveColors();
    const step = () => {
        if (!session) return;
        const reviewing = state === 'review';
        const pos = (reviewing && deck) ? deck.position : 0;
        session.lanes.forEach((lane) => {
            const strip = el.mtLanes.querySelector('[data-lane="' + lane.id + '"]');
            if (!strip) return;
            const cv = strip.querySelector('[data-role=wave]');
            if (cv && cv.clientWidth) {
                const progress = (reviewing && deck && deck.duration > 0)
                    ? Math.min(1, pos / deck.duration) : null;
                drawWave(cv, lane.peaks, lane.muted ? colors.dim : lane.color, progress, colors.dim);
            }
            if (!reviewing) {
                const m = strip.querySelector('[data-role=meter]');
                const h = strip.querySelector('[data-role=hold]');
                const db = strip.querySelector('[data-role=db]');
                const vd = strip.querySelector('[data-role=verdict]');
                if (m) m.style.width = Math.round(meterPos(lane.recentPeak) * 100) + '%';
                if (h) h.style.insetInlineStart = Math.round(meterPos(lane.peakHold) * 100) + '%';
                if (db) db.textContent = fmtDb(lane.peakHold);
                if (vd) {
                    const v = lane.verdict();
                    if (vd.dataset.v !== v) {
                        vd.dataset.v = v;
                        vd.className = 'mt-lane-chip v-' + v;
                        vd.textContent = t('lvl_' + v);
                    }
                }
            }
        });
        waveRAF = requestAnimationFrame(step);
    };
    step();
}

function clock(ms) {
    const s = Math.max(0, Math.floor(ms / 1000));
    const p = (n) => (n < 10 ? '0' + n : '' + n);
    return p(Math.floor(s / 3600)) + ':' + p(Math.floor((s % 3600) / 60)) + ':' + p(s % 60);
}
function mmss(s) {
    const x = Math.max(0, Math.floor(s));
    return Math.floor(x / 60) + ':' + (x % 60 < 10 ? '0' : '') + (x % 60);
}

/* ── lane strips ────────────────────────────────────────────────────────── */
function buildLaneStrips() {
    if (!session) { el.mtLanes.innerHTML = ''; return; }
    const want = session.lanes.map((l) => l.id).join(',');
    if (el.mtLanes.dataset.built !== want) {
        el.mtLanes.innerHTML = session.lanes.map((lane) =>
            '<article class="mt-lane" data-lane="' + lane.id + '">' + laneMarkup(lane, t) + '</article>'
        ).join('');
        el.mtLanes.dataset.built = want;
    }
    el.mtLanes.dataset.state = state;
    updateGhost();
}

function laneById(id) { return session && session.lanes.find((l) => l.id === id); }

function onLaneClick(e) {
    const btn = e.target.closest('button[data-act]');
    if (!btn) return;
    const lane = laneById(btn.dataset.id);
    if (!lane) return;
    const act = btn.dataset.act;

    if (act === 'mute' || act === 'solo') {
        lane[act] = !lane[act];
        btn.classList.toggle('on', lane[act]);
        btn.setAttribute('aria-pressed', lane[act] ? 'true' : 'false');
        const strip = btn.closest('.mt-lane');
        if (strip) strip.classList.toggle('muted', lane.muted);
        if (deck) deck.applyMix();
        ae('lane_' + act, { on: lane[act] ? 1 : 0 });
    } else if (act === 'dl') {
        saveOne(lane, btn);
    }
}

function onLaneInput(e) {
    const inp = e.target;
    if (inp.classList.contains('mt-lane-name')) {
        const lane = laneById(inp.dataset.id);
        if (lane) lane.label = inp.value;
        return;
    }
    if (!inp.dataset.act) return;
    const lane = laneById(inp.dataset.id);
    if (!lane) return;
    if (inp.dataset.act === 'gain') lane.gain = parseInt(inp.value, 10) / 100;
    if (inp.dataset.act === 'pan') lane.pan = parseInt(inp.value, 10) / 100;
    if (deck) deck.applyMix();
}

function updateGhost() {
    const show = !session && !candidates.length;
    if (el.mtGhost.hidden === !show) return;
    el.mtGhost.hidden = !show;
}

/* ── review transport ───────────────────────────────────────────────────── */
async function onPlayToggle() {
    if (!deck) return;
    if (deck.playing) { deck.pause(); el.btnPlay.textContent = t('play'); return; }
    await deck.play();
    el.btnPlay.textContent = t('pause');
}

function onSeek(e) {
    if (!deck || !deck.duration) return;
    deck.seek(parseInt(e.target.value, 10) / 1000 * deck.duration);
    onDeckTick(deck.position, deck.duration);
}

function onDeckTick(pos, dur) {
    el.mtTime.textContent = mmss(pos) + ' / ' + mmss(dur || 0);
    if (document.activeElement !== el.rngSeek) {
        el.rngSeek.value = dur > 0 ? Math.round(pos / dur * 1000) : 0;
    }
}

/* ── markers ────────────────────────────────────────────────────────────── */
function addMarker() {
    if (!session) return;
    const at = (state === 'review' && deck) ? deck.position : session.elapsedMs / 1000;
    session.mark(at, '');
    renderMarkerList();
    ae('marker_add', { at: Math.round(at), during: state });
    window.__announce && window.__announce(t('marker_added', { time: mmss(at) }));
    if (floatWin) floatWin.render();
}

function renderMarkerList() {
    if (!session || !session.markers.length) {
        el.mtMarkList.hidden = true;
        el.mtMarkList.innerHTML = '';
        return;
    }
    el.mtMarkList.hidden = false;
    el.mtMarkList.innerHTML = session.markers.map((m, i) =>
        '<button type="button" class="mt-mk" data-at="' + m.at + '">'
        + '<b>' + mmss(m.at) + '</b> ' + esc(m.text || (t('marker') + ' ' + (i + 1))) + '</button>'
    ).join('');
}

el_bindMarkerJump();
function el_bindMarkerJump() {
    const list = $('mtMarkList');
    if (!list) return;
    list.addEventListener('click', (e) => {
        const b = e.target.closest('.mt-mk');
        if (!b || !deck) return;
        deck.seek(parseFloat(b.dataset.at));
        onDeckTick(deck.position, deck.duration);
    });
}

/* ── export ─────────────────────────────────────────────────────────────── */
function onWhatClick(e) {
    const b = e.target.closest('button[data-what]');
    if (!b) return;
    el.mtWhat.querySelectorAll('button').forEach((x) => {
        x.classList.toggle('on', x === b);
        x.setAttribute('aria-pressed', x === b ? 'true' : 'false');
    });
    updateExportPanel();
}

/** Bitrate only exists for the lossy formats — WAV and FLAC have none. */
function onFormatChange() {
    const lossy = el.selFmt.value === 'mp3' || el.selFmt.value === 'ogg';
    el.mtKbpsField.hidden = !lossy;
    if (session) session.kbps = parseInt(el.selKbps.value, 10) || 192;
    updateExportPanel();
}

function chosenWhat() {
    const on = el.mtWhat.querySelector('button.on');
    return (on && on.dataset.what) || 'stems';
}

function updateExportPanel() {
    if (!session) return;
    const done = session.doneLanes;
    el.mtExport.hidden = state !== 'review';
    if (state !== 'review') return;
    el.mtKbpsField.hidden = !(el.selFmt.value === 'mp3' || el.selFmt.value === 'ogg');

    const fmt = el.selFmt.value;
    const dur = deck ? deck.duration : 0;
    const what = chosenWhat();
    const needsMix = what !== 'stems';
    const fits = !needsMix || mixFits(fmt, dur, session.rate, session.kbps);

    el.btnSave.disabled = !fits;
    if (!fits) {
        const gb = (estimateMixBytes(fmt, dur, session.rate, session.kbps) / 1073741824).toFixed(1);
        expText(t('mix_too_big', { size: gb }), true);
    } else {
        const n = (what === 'mix' ? 1 : done.length + (what === 'both' ? 1 : 0))
            + (session.markers.length ? 1 : 0);   // the session sheet is a file too
        expText(t('will_save', { n }), false);
    }
}

function expText(msg, bad) {
    el.mtExpText.textContent = msg;
    el.mtExpText.classList.toggle('bad', !!bad);
}

/**
 * The cap unit is the SESSION. One recording is one run, whatever the user
 * pulls out of it — stems, a mix, the cue sheet, a second pass in another
 * format. Billing per file made a three-mic take cost three runs, which put
 * the tool's own minimum job past a verified account's flat cap of two.
 */
function beginRun() {
    if (!window.__toolLimit) return { commit() {}, abort() {} };
    return window.__toolLimit.begin(SLUG, 'sess:' + session.id);
}

async function saveOne(lane, btn) {
    const tok = beginRun();
    if (!tok) return;                       // wall shown by the primitive
    btn.disabled = true;
    try {
        const fmt = el.selFmt.value;
        const blob = await renderStem(lane, fmt, { rate: session.rate, kbps: session.kbps });
        const name = planNames([lane], FORMATS[fmt].ext, 'track')[0];
        await window.__downloadFile(blob, name, { src: 'multi-track-stem' });
        tok.commit();
        session.saved = true;
        ae('save_one', { fmt });
        try { window.__aeToolComplete({ file: name, sizeMB: Math.round(blob.size / 1048576) }); } catch (_) {}
    } catch (err) {
        tok.abort();
        window.__handleError && window.__handleError(err, { stage: 'stem' }, { say: t('export_failed') });
    } finally {
        btn.disabled = false;
    }
}

async function onSave() {
    if (!session || state !== 'review') return;
    const tok = beginRun();
    if (!tok) return;

    let lone = null;
    const what = chosenWhat();
    const fmt = el.selFmt.value;
    const ext = FORMATS[fmt].ext;
    const done = session.doneLanes;
    const opts = { rate: session.rate, kbps: session.kbps };
    const names = planNames(done, ext, 'track');

    /* Plan the artefacts BEFORE producing any, so the delivery lane is decided
       by a count and not by a branch inside a loop. The browser hands out ONE
       file per user gesture and silently drops the rest — and a tab that tried
       loses downloads for the whole origin afterwards — so a loop that can
       reach __downloadFile is a defect even when today's inputs happen to make
       it run once. */
    const plan = [];
    if (what !== 'mix') {
        done.forEach((lane, i) => plan.push({ name: names[i], make: () => renderStem(lane, fmt, opts) }));
    }
    if (what !== 'stems') {
        plan.push({ name: 'mix.' + ext, make: () => renderMix(done, fmt, opts, () => {}) });
    }
    const wantCue = session.markers.length > 0;
    const total = plan.length + (wantCue ? 1 : 0);

    el.btnSave.disabled = true;
    el.mtExpProg.hidden = false;
    progress(0, t('preparing'));

    /* __batchDeliver FIRST — the folder picker lives on the click's user
       activation, and any await before it (an encode, a worker, a fetch)
       spends that activation and silently demotes us to a single archive. It
       also keeps ONE file in memory at a time, which is the only reason a long
       multi-lane session can be delivered at all. */
    let batch = null;
    if (total > 1) {
        batch = await window.__batchDeliver.openBatch({
            slug: SLUG,
            zipName: 'multitrack-' + stamp() + '.zip',
            total,
        });
    }

    let delivered = 0;
    try {
        for (let i = 0; i < plan.length; i++) {
            progress((i / (plan.length + 1)) * 0.92,
                plan[i].name.indexOf('mix.') === 0
                    ? t('mixing') : t('converting_n', { i: i + 1, n: done.length }));
            const blob = await plan[i].make();
            if (batch) await batch.write(plan[i].name, blob);
            else lone = { blob, name: plan[i].name };     // exactly one, delivered below
            delivered++;
        }
        if (batch && wantCue) {
            await batch.write('session.txt', buildCueSheet(session, t, { rate: session.rate }));
            delivered++;
        }

        progress(1, t('saving'));
        let via = 'single';
        if (batch) { const r = await batch.finish(); via = r.via; }
        else if (lone) await window.__downloadFile(lone.blob, lone.name, { src: 'multi-track' });

        if (via === 'refused') { tok.abort(); expText(t('export_failed'), true); }
        else {
            tok.commit();
            session.saved = true;
            expText(t('saved_n', { n: delivered }), false);
            try {
                window.__aeToolComplete({
                    file: what + '.' + ext,
                    sizeMB: Math.round(session.totalBytes() / 1048576),
                });
            } catch (_) {}
        }
        ae('save_batch', { what, fmt, n: delivered, via, tracks: done.length, markers: session.markers.length });
    } catch (err) {
        if (batch) { try { batch.abort(); } catch (_) {} }
        tok.abort();
        window.__handleError && window.__handleError(err, { stage: 'export' });
        expText(t('export_failed'), true);
        ae('save_failed', { what, fmt, err: String((err && err.message) || err).slice(0, 60) });
    } finally {
        el.btnSave.disabled = false;
        setTimeout(() => { el.mtExpProg.hidden = true; }, 2500);
    }
}

function progress(p, msg) {
    el.mtExpBar.style.width = Math.round(Math.max(0, Math.min(1, p)) * 100) + '%';
    if (msg) expText(msg, false);
}

function stamp() {
    const d = new Date();
    const p = (n) => (n < 10 ? '0' + n : '' + n);
    return d.getFullYear() + p(d.getMonth() + 1) + p(d.getDate()) + '-' + p(d.getHours()) + p(d.getMinutes());
}

/* ── floating transport ─────────────────────────────────────────────────── */
async function onFloat() {
    if (floatWin) { floatWin.close(); floatWin = null; return; }
    try {
        floatWin = await openFloatingTransport({
            getState: () => ({
                recording: state === 'recording' || state === 'paused',
                paused: state === 'paused',
                elapsedMs: session ? session.elapsedMs : 0,
                lanes: (session ? session.lanes : []).map((l) => ({
                    label: l.label,
                    meter: meterPos(l.recentPeak),
                    hold: meterPos(l.peakHold),
                    dbText: fmtDb(l.peakHold),
                    verdict: l.verdict(),
                    dead: l.state === 'failed',
                })),
            }),
            onStop: () => stopRecording(),
            onPause: () => onPauseToggle(),
            onMark: () => addMarker(),
            onClosed: () => { floatWin = null; el.btnFloat.setAttribute('aria-pressed', 'false'); },
            labels: {
                title: t('float_title'), stop: t('stop'), pause: t('pause'),
                resume: t('resume'), mark: t('mark'), rec: t('state_rec'),
                paused: t('state_paused'), ready: t('state_ready'),
            },
        });
        el.btnFloat.setAttribute('aria-pressed', 'true');
        ae('float_open');
    } catch (err) {
        ae('float_failed', { err: String((err && err.name) || err).slice(0, 30) });
        window.__toast && window.__toast(t('float_failed'), 'warning');
    }
}

function closeFloat() { if (floatWin) { floatWin.close(); floatWin = null; } el.btnFloat.setAttribute('aria-pressed', 'false'); }

/* ── monitoring ─────────────────────────────────────────────────────────── */
function attachMonitor() {
    try {
        if (!window.MicMonitor || !session) return;
        monitor = window.MicMonitor.attach(session.ctx, {
            tool: 'mic-monitor',
            source: session.monitorBus,
            mount: el.mtMonSlot,
            labels: { label: t('monitor'), hint: t('monitor_hint') },
        });
    } catch (_) {}
}
function detachMonitor() { try { if (monitor) monitor.detach(); } catch (_) {} monitor = null; }

/* ── wake lock ──────────────────────────────────────────────────────────── */
function acquireWake() { try { if (window.__wakeLock) window.__wakeLock.request(); } catch (_) {} }
function releaseWake() { try { if (window.__wakeLock) window.__wakeLock.release(); } catch (_) {} }

/* ── countdown ──────────────────────────────────────────────────────────── */
function countdown() {
    return new Promise((resolve) => {
        let n = 3;
        el.mtTimer.classList.add('count');
        const tick = () => {
            if (state === 'recording') { done(false); return; }
            el.mtTimer.textContent = String(n);
            window.__announce && window.__announce(String(n));
            if (n-- <= 0) { done(true); return; }
            setTimeout(tick, 1000);
        };
        const done = (ok) => {
            el.mtTimer.classList.remove('count');
            el.mtTimer.textContent = '00:00:00';
            resolve(ok);
        };
        tick();
    });
}

/* ── crash recovery ─────────────────────────────────────────────────────── */
async function offerRecovery() {
    // Orphans can exist from an older visit even if THIS one is not durable,
    // so the scan runs whenever OPFS exists at all.
    if (!opfsAvailable()) return;
    const found = await findOrphanSessions('');
    if (!found.length) return;
    restoreOffer = found[0];
    // A short take rounds to "0 МБ", which reads as "nothing was saved" — the
    // exact opposite of what this banner exists to say.
    const bytes = restoreOffer.lanes.reduce((n, l) => n + l.file.size, 0);
    const mb = bytes >= 1048576 ? Math.round(bytes / 1048576) + ' ' + t('mb')
        : Math.max(1, Math.round(bytes / 1024)) + ' ' + t('kb');
    el.mtRestoreText.textContent = t('restore_found', {
        n: restoreOffer.lanes.length, mb,
        when: new Date(restoreOffer.at).toLocaleString(window.__LOCALE || undefined),
    });
    el.mtRestore.hidden = false;
    ae('restore_offered', { lanes: restoreOffer.lanes.length, mb });

    el.btnRestore.addEventListener('click', doRestore, { once: true });
    el.btnDropRestore.addEventListener('click', async () => {
        el.mtRestore.hidden = true;
        await dropSession(restoreOffer.id);
        restoreOffer = null;
        ae('restore_discarded');
    }, { once: true });
}

async function doRestore() {
    if (!restoreOffer) return;
    el.mtRestore.hidden = true;
    session = new Session(restoreOffer.id);
    session.rate = parseInt(el.selRate.value, 10) || 48000;
    session.kbps = parseInt(el.selKbps.value, 10) || 192;
    session.ensureContext(session.rate);

    restoreOffer.lanes.forEach((entry, i) => {
        const lane = Object.create(Lane.prototype);
        Object.assign(lane, {
            id: 'L' + i, kind: 'mic', label: t('track') + ' ' + (i + 1),
            color: COLORS[i % COLORS.length], ctx: session.ctx,
            gain: 1, pan: 0, muted: false, solo: false,
            peaks: [], peakHold: 0, clipWindows: 0, recentPeak: 0, recentRms: 0,
            durationSec: 0, sawAudio: true, file: entry.file, state: 'done',
            settings: { sampleRate: 0, channelCount: 0 }, error: null,
            sink: { bytes: entry.file.size },
        });
        session.lanes.push(lane);
    });

    /* Peaks FIRST. A restored session has no live envelope — it was never
       metered in this tab — and the deck's duration fallback is exactly that
       envelope, because a MediaRecorder WebM carries no duration in its
       header. Loading before sketching left the transport reading
       "0:00 / 0:00" on a take that was nine seconds long. */
    deck = new ReviewDeck(session.ctx);
    deck.onTick = onDeckTick;
    deck.onEnd = () => { el.btnPlay.textContent = t('play'); };
    await sketchPeaksFromFiles(session.doneLanes);
    await deck.load(session.doneLanes);
    setState('review');
    buildLaneStrips();
    scheduleWaveDraw();
    updateExportPanel();
    onDeckTick(0, deck.duration);
    ae('restore_done', { lanes: session.lanes.length });
    notice(t('restore_done'), 'good');
}

/**
 * Peaks for a restored file. Decoding a whole take would reintroduce exactly
 * the memory problem this design removed, so only the first 90 seconds are
 * decoded — enough for the strip to show a real shape, bounded by design.
 */
async function sketchPeaksFromFiles(lanes) {
    if (!session || !session.ctx) return;
    for (const lane of lanes) {
        try {
            const slice = lane.file.slice(0, Math.min(lane.file.size, 12 * 1024 * 1024));
            const buf = await session.ctx.decodeAudioData(await slice.arrayBuffer());
            const ch = buf.getChannelData(0);
            const step = Math.max(1, Math.floor(buf.sampleRate / 20));
            const out = [];
            for (let i = 0; i < ch.length; i += step) {
                let m = 0;
                for (let j = i; j < Math.min(i + step, ch.length); j++) {
                    const a = Math.abs(ch[j]); if (a > m) m = a;
                }
                out.push(m);
                if (m > lane.peakHold) lane.peakHold = m;
            }
            lane.peaks = out;
        } catch (_) { /* a shapeless strip is better than a failed restore */ }
    }
}


/**
 * Register the level-meter worklet on one context. Uses the shared
 * AudioWorkletLoader (multi-URL + retry) rather than a bare addModule: the
 * measured failure mode is a transient mobile-network blip on the first
 * fetch, which a hardcoded single URL turns into a permanently dead meter.
 */
async function loadMeter(ctx) {
    try {
        if (!window.AudioWorkletNode || !ctx.audioWorklet) return false;
        const urls = (CFG.meterUrls || []).filter(Boolean);
        if (!urls.length) return false;
        if (window.AudioWorkletLoader) {
            await window.AudioWorkletLoader.load(ctx, urls, { slug: SLUG });
        } else {
            await ctx.audioWorklet.addModule(urls[0]);
        }
        return true;
    } catch (err) {
        ae('meter_unavailable', { err: String((err && err.message) || err).slice(0, 60) });
        return false;
    }
}

/* ── misc ───────────────────────────────────────────────────────────────── */
function setState(s) {
    state = s;
    el.mtRoot.dataset.state = s;
    const rec = s === 'recording', paused = s === 'paused', review = s === 'review';
    el.mtState.textContent = t('state_' + (review ? 'review' : (paused ? 'paused' : (rec ? 'rec' : (s === 'finishing' ? 'finishing' : 'ready')))));
    el.btnRec.hidden = rec || paused || review || s === 'finishing';
    el.btnAgain.hidden = !review;
    el.btnPause.hidden = !(rec || paused);
    el.btnStop.hidden = !(rec || paused);
    el.btnMark.hidden = !(rec || paused || review);
    el.btnFloat.hidden = !pipSupported() || review || s === 'finishing';
    el.mtScrub.hidden = !review;
    el.mtExport.hidden = !review;
    el.btnPause.setAttribute('aria-label', paused ? t('resume') : t('pause'));
    el.btnPause.classList.toggle('resume', paused);
    if (session) {
        const bits = [
            t('n_tracks', { n: session.lanes.length }),
            session.rate ? (session.rate / 1000) + ' ' + t('khz') : '',
            sizeLabel(session.totalBytes()),
        ].filter(Boolean);
        el.mtMeta.textContent = bits.join(' · ');
    } else {
        el.mtMeta.textContent = '';
    }
    updateRecButton();
}

/** Bytes for the transport line: MB once there is a whole one, else nothing.
    "0 МБ" next to a running timer reads as "nothing is being recorded". */
function sizeLabel(bytes) {
    if (!bytes || bytes < 1048576) return '';
    return Math.round(bytes / 1048576) + ' ' + t('mb');
}

function notice(msg, kind) {
    el.mtNotice.textContent = msg;
    el.mtNotice.className = 'tc-msg ' + (kind || '');
    el.mtNotice.hidden = false;
}
function clearNotice() { el.mtNotice.hidden = true; }

function onKey(e) {
    const tag = e.target.tagName;
    if (window.__keyOwnedByFocus ? window.__keyOwnedByFocus(e) : (tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT' || e.target.isContentEditable)) return;
    if (e.metaKey || e.ctrlKey || e.altKey) return;
    if (e.code === 'Space' && (state === 'recording' || state === 'paused')) { e.preventDefault(); onPauseToggle(); }
    else if (e.code === 'Space' && state === 'review') { e.preventDefault(); onPlayToggle(); }
    else if (e.code === 'Escape' && (state === 'recording' || state === 'paused')) { e.preventDefault(); stopRecording(); }
    else if ((e.code === 'KeyM') && (state === 'recording' || state === 'paused' || state === 'review')) { e.preventDefault(); addMarker(); }
    else if (e.code === 'KeyR' && (state === 'ready')) { e.preventDefault(); onRecord(); }
}

function onUnload(e) {
    if (state === 'recording' || state === 'paused'
        || (state === 'review' && session && !session.saved)) {
        e.preventDefault();
        e.returnValue = '';
        return '';
    }
}
